import React from 'react';
import styled from 'styled-components';

const Section = styled.section`
  padding: clamp(2.75rem, 5.5vw, 4.25rem) clamp(1.25rem, 4vw, 2rem);
  border-top: 1px solid rgba(16, 29, 64, 0.07);
  background: ${({ $imageRight, theme }) =>
    $imageRight
      ? `linear-gradient(
          180deg,
          ${theme.colors.offWhite} 0%,
          rgba(241, 239, 234, 0.55) 48%,
          ${theme.colors.offWhite} 100%
        )`
      : theme.colors.offWhite};
  overflow: hidden;
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: clamp(2rem, 5vw, 4rem);
  flex-direction: ${({ $imageRight }) => ($imageRight ? 'row-reverse' : 'row')};

  @media (max-width: 820px) {
    flex-direction: column;
    gap: 1.75rem;
  }
`;

const ImageWrap = styled.div`
  flex: 0 0 52%;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  filter: drop-shadow(0 18px 38px rgba(16, 29, 64, 0.14))
    drop-shadow(0 4px 12px rgba(16, 29, 64, 0.08));

  @media (max-width: 820px) {
    flex: none;
    width: 100%;
  }
`;

const Image = styled.img`
  display: block;
  width: 100%;
  height: clamp(260px, 32vw, 400px);
  object-fit: cover;
  clip-path: ${({ $imageRight }) =>
    $imageRight
      ? 'polygon(14% 0%, 100% 0%, 100% 100%, 0% 100%)'
      : 'polygon(0% 0%, 100% 0%, 86% 100%, 0% 100%)'};

  @media (max-width: 820px) {
    height: 220px;
    clip-path: none;
    border-radius: 14px;
  }
`;

const TextSide = styled.div`
  flex: 1;
  min-width: 0;
`;

const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.85rem;
`;

const EyebrowLine = styled.span`
  display: block;
  width: 2rem;
  height: 1.5px;
  background: ${({ theme }) => theme.colors.accent};
  border-radius: 2px;
  opacity: 0.8;
`;

const Eyebrow = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
`;

const Title = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.65rem, 3.8vw, 2.35rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  line-height: 1.12;
`;

const Description = styled.div`
  color: rgba(16, 29, 64, 0.68);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.95rem, 1.85vw, 1.05rem);
  line-height: 1.65;
  margin-bottom: 1.5rem;
`;

const BookBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 1.75rem;
  border-radius: 999px;
  font-family: ${props => props.theme.fonts.main};
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  color: #fff;
  background: ${props => props.theme.colors.accent};
  border: 2px solid ${props => props.theme.colors.accent};
  transition: background 0.25s ease, border-color 0.25s ease, color 0.25s ease,
    transform 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const PricingPanel = styled.div`
  margin-top: clamp(1.75rem, 4vw, 2.5rem);
  padding: clamp(1.35rem, 3vw, 1.85rem);
  border-radius: 18px;
  border: 1px solid rgba(16, 29, 64, 0.08);
  background: #fff;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 12px 40px rgba(16, 29, 64, 0.07);
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.85rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const PricingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 1.15rem 1.1rem;
  border-radius: 14px;
  background: linear-gradient(
    165deg,
    rgba(16, 29, 64, 0.028) 0%,
    rgba(241, 239, 234, 0.45) 100%
  );
  border: 1px solid rgba(16, 29, 64, 0.07);
`;

const TicketType = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};
  font-size: clamp(1.05rem, 2vw, 1.2rem);
  font-weight: 700;
  margin: 0 0 0.85rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(16, 29, 64, 0.08);
  text-align: left;
`;

const OfficeMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const OfficeLabel = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.42);
`;

const OfficePrice = styled.div`
  font-family: ${props => props.theme.fonts.main};
  font-size: ${props => props.$compact
    ? 'clamp(1.05rem, 2vw, 1.2rem)'
    : 'clamp(1.5rem, 3vw, 1.85rem)'};
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  letter-spacing: -0.02em;
  line-height: 1.25;
`;

const InfoDetail = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.84rem, 1.6vw, 0.92rem);
  line-height: 1.58;
  color: rgba(16, 29, 64, 0.62);
  margin: 0.65rem 0 0;
`;

const PanelHeading = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1rem, 2vw, 1.1rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  text-align: left;
`;

const PanelFootnote = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8125rem;
  line-height: 1.55;
  color: rgba(16, 29, 64, 0.52);
  margin: 1rem 0 0;
  padding-top: 1rem;
  border-top: 1px solid rgba(16, 29, 64, 0.08);
  text-align: left;
`;

const ImageSlantWithPricing = ({
  image,
  title,
  description,
  buttonText,
  buttonAction,
  imagePosition = 'left',
  pricingData,
  eyebrow,
  pricingVariant = 'pricing',
  pricingPanelTitle,
  pricingFootnote,
}) => {
  const imageRight = imagePosition === 'right';

  return (
    <Section $imageRight={imageRight}>
      <Inner $imageRight={imageRight}>
        <ImageWrap>
          <Image src={image} alt={title} $imageRight={imageRight} loading="lazy" />
        </ImageWrap>
        <TextSide>
          <EyebrowRow>
            <EyebrowLine aria-hidden />
            <Eyebrow>{eyebrow || 'Tickets'}</Eyebrow>
          </EyebrowRow>
          <Title>{title}</Title>
          <Description dangerouslySetInnerHTML={{ __html: description }} />
          <BookBtn type="button" onClick={buttonAction}>
            {buttonText}
          </BookBtn>

          {pricingData?.length > 0 && (
            <PricingPanel>
              {pricingPanelTitle && (
                <PanelHeading>{pricingPanelTitle}</PanelHeading>
              )}
              <PricingGrid>
                {pricingData.map((ticket, index) => (
                  <PricingCard key={index}>
                    <TicketType>{ticket.type}</TicketType>
                    <OfficeMeta>
                      {pricingVariant === 'info' ? (
                        <>
                          {ticket.officePrice && (
                            <OfficePrice $compact={Boolean(ticket.detail)}>
                              {ticket.officePrice}
                            </OfficePrice>
                          )}
                          {ticket.detail && <InfoDetail>{ticket.detail}</InfoDetail>}
                        </>
                      ) : (
                        <>
                          <OfficeLabel>{ticket.metaLabel || 'Ticket office'}</OfficeLabel>
                          <OfficePrice>{ticket.officePrice}</OfficePrice>
                        </>
                      )}
                    </OfficeMeta>
                  </PricingCard>
                ))}
              </PricingGrid>
              {pricingFootnote && <PanelFootnote>{pricingFootnote}</PanelFootnote>}
            </PricingPanel>
          )}
        </TextSide>
      </Inner>
    </Section>
  );
};

export default ImageSlantWithPricing;
